import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Send, Sparkles, X } from 'lucide-react';
import type { ModelContextChatMessage, StructuredModelContext } from './modelContextTypes';
import { copilotContextService } from './copilotContextService';
import {
    createEmptyDraft,
    finalizeContext,
    formatContextNarrative,
    logContextCopilot,
    parseMetadataSummary,
} from './modelContextSyntheticEngine';

const FINALIZE_DELAY_MS = 350;
const WORKING_ROTATE_MS = 1600;
const WORKING_LINES = [
    'Reading the metadata you passed...',
    'Mapping fields, files, and structure...',
    'Writing descriptions agents can use...',
    'Checking names against the JSON...',
];

interface ModelContextWorkspaceProps {
    metadataStructure: string;
    modelName: string;
    sourceType: string;
    initialDraft?: StructuredModelContext | null;
    onClose: (draft: StructuredModelContext | null) => void;
    onFinalize: (context: StructuredModelContext) => void;
}

const ModelContextWorkspace: React.FC<ModelContextWorkspaceProps> = ({
    metadataStructure,
    modelName,
    sourceType,
    initialDraft = null,
    onClose,
    onFinalize,
}) => {
    const summary = useMemo(() => parseMetadataSummary(metadataStructure), [metadataStructure]);
    const [visible, setVisible] = useState(false);
    const [draft, setDraft] = useState<StructuredModelContext>(() => {
        const base = initialDraft ?? createEmptyDraft(summary, modelName, sourceType, metadataStructure);
        return { ...base, sourceMetadata: metadataStructure || base.sourceMetadata || '' };
    });
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [followUps, setFollowUps] = useState<ModelContextChatMessage[]>(
        () => initialDraft?.followUpMessages ?? []
    );
    const [followUpInput, setFollowUpInput] = useState('');
    const [isFollowUpTyping, setIsFollowUpTyping] = useState(false);
    const [workspaceError, setWorkspaceError] = useState<string | null>(null);
    const followUpEndRef = useRef<HTMLDivElement | null>(null);
    const autoAnalyzeStartedRef = useRef(false);
    const draftRef = useRef(draft);
    draftRef.current = draft;

    const narrative = (draft.contextNarrative || draft.analysisSummary || '').trim();
    const canFinalize = Boolean(narrative) && !isAnalyzing && !isFinalizing && !isFollowUpTyping;

    const applyNarrative = (text: string) => {
        const formatted = formatContextNarrative(text);
        const next: StructuredModelContext = {
            ...draftRef.current,
            status: 'draft',
            contextNarrative: formatted,
            analysisSummary: formatted,
            purpose: formatted,
            sourceMetadata: metadataStructure,
            selectedEntityId: 'all',
            llmInstructions: '',
        };
        draftRef.current = next;
        setDraft(next);
    };

    const runAnalyze = async (followUp?: string) => {
        if (isAnalyzing || isFinalizing) return null;
        setIsAnalyzing(true);
        setWorkspaceError(null);
        logContextCopilot('app', 'analyze_started', {
            entityId: 'all',
            modelName,
            metadataChars: metadataStructure.length,
            hasFollowUp: Boolean(followUp?.trim()),
        });
        try {
            const analysis = await copilotContextService.analyzeContext({
                metadataStructure,
                entityId: 'all',
                modelName,
                followUp,
                priorAnalysis: followUp
                    ? (draftRef.current.contextNarrative || draftRef.current.analysisSummary)
                    : undefined,
            });
            applyNarrative(analysis.contextNarrative);
            return analysis;
        } catch (err) {
            setWorkspaceError(err instanceof Error ? err.message : 'Failed to analyze context');
            return null;
        } finally {
            setIsAnalyzing(false);
        }
    };

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(() => setVisible(true));
        const resumed = Boolean((initialDraft?.contextNarrative || initialDraft?.analysisSummary || '').trim());
        logContextCopilot('app', 'workspace_opened', {
            modelName,
            sourceType,
            resumed,
            metadataChars: metadataStructure.length,
        });
        if (!resumed && metadataStructure.trim() && !autoAnalyzeStartedRef.current) {
            autoAnalyzeStartedRef.current = true;
            void runAnalyze();
        }
        return () => {
            document.body.style.overflow = '';
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const closeWith = (next: StructuredModelContext | null) => {
        setVisible(false);
        window.setTimeout(() => onClose(next), 180);
    };

    const handleCloseKeepDraft = () => {
        closeWith({
            ...draftRef.current,
            followUpMessages: followUps,
            sourceMetadata: metadataStructure,
            status: 'draft',
        });
    };

    const handleDiscard = () => {
        logContextCopilot('app', 'workspace_discarded', { modelName });
        closeWith(null);
    };

    const sendFollowUp = async () => {
        const trimmed = followUpInput.trim();
        if (!trimmed || isFollowUpTyping || isAnalyzing || isFinalizing) return;

        const userMsg: ModelContextChatMessage = {
            id: `fu-${Date.now()}`,
            role: 'user',
            content: trimmed,
            createdAt: new Date().toISOString(),
        };
        setFollowUps((prev) => [...prev, userMsg]);
        setFollowUpInput('');
        setIsFollowUpTyping(true);
        setWorkspaceError(null);

        try {
            const analysis = await copilotContextService.analyzeContext({
                metadataStructure,
                entityId: 'all',
                modelName,
                followUp: trimmed,
                priorAnalysis: draftRef.current.contextNarrative || draftRef.current.analysisSummary,
            });
            applyNarrative(analysis.contextNarrative);
            const assistantMsg: ModelContextChatMessage = {
                id: `fu-${Date.now()}-ai`,
                role: 'assistant',
                content: 'Updated the additional context from your guidance.',
                createdAt: new Date().toISOString(),
            };
            const nextFollowUps = [...followUps, userMsg, assistantMsg];
            draftRef.current = { ...draftRef.current, followUpMessages: nextFollowUps };
            setDraft(draftRef.current);
            setFollowUps(nextFollowUps);
        } catch (err) {
            setWorkspaceError(err instanceof Error ? err.message : 'Follow-up failed');
            setFollowUps((prev) => prev.filter((m) => m.id !== userMsg.id));
        } finally {
            setIsFollowUpTyping(false);
        }
    };

    useEffect(() => {
        followUpEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [followUps, isFollowUpTyping]);

    const handleFinalize = () => {
        if (!canFinalize) return;
        setIsFinalizing(true);
        const withChat = {
            ...draftRef.current,
            followUpMessages: followUps,
            sourceMetadata: metadataStructure,
            contextNarrative: narrative,
            analysisSummary: narrative,
        };
        draftRef.current = withChat;
        window.setTimeout(() => {
            const finalized = finalizeContext(withChat, summary);
            setIsFinalizing(false);
            setVisible(false);
            window.setTimeout(() => onFinalize(finalized), 180);
        }, FINALIZE_DELAY_MS);
    };

    return createPortal(
        <div className={`dm-context-overlay fixed inset-0 z-[120] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="dm-context relative w-full max-w-2xl rounded-2xl overflow-hidden flex flex-col max-h-[86vh]">
                <header className="dm-context__header flex items-start justify-between gap-3 px-5 py-4 border-b shrink-0">
                    <div>
                        <h2 className="dm-context__title font-medium text-[15px] tracking-tight">Additional context</h2>
                        <p className="dm-context__subtitle text-[12px] mt-0.5">
                            {modelName || 'Untitled model'}
                            {sourceType ? ` · ${sourceType}` : ''}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleCloseKeepDraft}
                        className="dm-context__close p-1.5 rounded-md transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </header>

                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-5 py-4 space-y-4">
                    {(isAnalyzing || isFinalizing) && (
                        <AiWorkingPanel mode={isFinalizing ? 'finalize' : 'analyze'} />
                    )}

                    {!isAnalyzing && !isFinalizing && narrative && (
                        <section className="space-y-3">
                            <p className="dm-context__muted text-[13px]">
                                I found the following in the metadata you passed.
                            </p>
                            <div className="dm-context__panel rounded-xl px-4 py-3.5">
                                <ContextNarrativeView text={narrative} />
                            </div>
                        </section>
                    )}

                    {narrative && !isFinalizing && (
                        <section className="space-y-2.5">
                            {followUps.filter((m) => m.role === 'user').map((msg) => (
                                <p key={msg.id} className="dm-context__muted text-[12px]">
                                    You: {msg.content}
                                </p>
                            ))}
                            {isFollowUpTyping && (
                                <div className="dm-context__muted flex items-center gap-2 text-[12px]">
                                    <span className="inline-flex gap-0.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" />
                                        <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:120ms]" />
                                        <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:240ms]" />
                                    </span>
                                    Updating context...
                                </div>
                            )}
                            <div ref={followUpEndRef} />
                            <form
                                className="flex gap-2"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    sendFollowUp();
                                }}
                            >
                                <input
                                    value={followUpInput}
                                    onChange={(e) => setFollowUpInput(e.target.value)}
                                    disabled={isFollowUpTyping || isAnalyzing || isFinalizing}
                                    placeholder="Guide the copilot, or finalize if this looks right"
                                    className="dm-context__input flex-1 rounded-lg px-3 py-2 text-sm outline-none disabled:opacity-60"
                                />
                                <button
                                    type="submit"
                                    disabled={!followUpInput.trim() || isFollowUpTyping || isAnalyzing || isFinalizing}
                                    className="dm-context__icon-btn inline-flex items-center justify-center w-9 h-9 rounded-lg disabled:opacity-40 disabled:pointer-events-none"
                                    aria-label="Send"
                                >
                                    <Send className="w-3.5 h-3.5" />
                                </button>
                            </form>
                        </section>
                    )}
                </div>

                <footer className="dm-context__footer shrink-0 border-t px-5 py-3 flex items-center justify-between gap-3">
                    <p className="text-[11px] text-red-500 min-h-[1rem]">{workspaceError || ''}</p>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleDiscard}
                            className="dm-context__ghost text-[12px] px-3 py-1.5 rounded-lg"
                        >
                            Discard
                        </button>
                        <button
                            type="button"
                            onClick={handleFinalize}
                            disabled={!canFinalize}
                            className="dm-context__primary inline-flex items-center gap-1.5 text-[12px] font-medium px-3.5 py-1.5 rounded-lg disabled:opacity-40 disabled:pointer-events-none"
                        >
                            {isFinalizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                            Finalize context
                        </button>
                    </div>
                </footer>
            </div>
        </div>,
        document.body
    );
};

function ContextNarrativeView({ text }: { text: string }) {
    const formatted = formatContextNarrative(text);
    const lines = formatted.split('\n');
    return (
        <div className="dm-context-narrative">
            {lines.map((line, index) => {
                const trimmed = line.trim();
                if (!trimmed) {
                    return <div key={index} className="h-2" />;
                }
                const isLabel = /^(columns|fields|files|folders|use this)\s*:?$/i.test(trimmed);
                const isObjectHeading =
                    !trimmed.startsWith('- ') &&
                    trimmed.length < 80 &&
                    (trimmed.endsWith(':') || /^(table|view|collection|query|entity)\s*:/i.test(trimmed));
                const isBareHeading =
                    !trimmed.startsWith('- ') &&
                    trimmed.length < 72 &&
                    !/[.!?]$/.test(trimmed) &&
                    !trimmed.includes(': ');
                if (isLabel || isObjectHeading || isBareHeading) {
                    return (
                        <p key={index} className="dm-context-narrative__heading">
                            {trimmed.replace(/:$/, '')}
                        </p>
                    );
                }
                if (trimmed.startsWith('- ')) {
                    const body = trimmed.slice(2);
                    const colon = body.indexOf(':');
                    if (colon > 0 && colon < 80) {
                        return (
                            <p key={index} className="dm-context-narrative__item">
                                <span className="dm-context-narrative__name">{body.slice(0, colon)}</span>
                                {body.slice(colon)}
                            </p>
                        );
                    }
                    return (
                        <p key={index} className="dm-context-narrative__item">
                            {body}
                        </p>
                    );
                }
                return (
                    <p key={index} className="dm-context-narrative__p">
                        {trimmed}
                    </p>
                );
            })}
        </div>
    );
}

function AiWorkingPanel({ mode }: { mode: 'analyze' | 'finalize' }) {
    const [lineIndex, setLineIndex] = useState(0);
    useEffect(() => {
        const id = window.setInterval(() => {
            setLineIndex((i) => (i + 1) % WORKING_LINES.length);
        }, WORKING_ROTATE_MS);
        return () => window.clearInterval(id);
    }, []);
    const headline = mode === 'finalize' ? 'Compiling additional context...' : WORKING_LINES[lineIndex];
    return (
        <div className="dm-context__working rounded-xl px-4 py-4">
            <div className="flex items-start gap-3">
                <div className="relative shrink-0 mt-0.5">
                    <span className="absolute inset-0 rounded-full bg-current opacity-20 animate-ping" />
                    <Sparkles className="relative w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-[13px]">{headline}</p>
                    <div className="mt-3 space-y-2">
                        <div className="dm-context__skeleton h-2 w-10/12 rounded animate-pulse" />
                        <div className="dm-context__skeleton h-2 w-7/12 rounded animate-pulse" />
                        <div className="dm-context__skeleton h-2 w-9/12 rounded animate-pulse" />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ModelContextWorkspace;
